export const en = {
  meta: { locale: 'en-GB' },
  nav: {
    stable: 'Stable',
    horses: 'Horses',
    rides: 'Rides & prices',
    booking: 'Booking',
    events: 'Events',
    rules: 'Good to know',
    book: 'Book a ride',
    menu: 'Menu',
  },
  hero: {
    location: '25 km from Tallinn · 30 minutes from the airport',
    title: 'Arabian horses, pine forest and the sea',
    subtitle:
      'Guided horseback rides for every level — from a first calm walk to a canter along the Baltic shore. Small groups, kind horses, times set by daylight.',
    cta: 'Book a ride',
    secondary: 'Meet the horses',
  },
  notice: {
    rides: 'Online booking for rides from 1 November 2026.',
    events: 'Weddings and photo sessions for the 2027 season (from April) — reserve your date now.',
    link: 'Request a date',
  },
  gallery: {
    title: 'The stable',
    intro:
      'A wooden stable with 16 boxes on 16.5 hectares of our own land — pastures, forest nearby and quiet countryside just 25 km from Tallinn.',
    open: 'Open photo',
    more: '+{n} photos',
    close: 'Close',
    prev: 'Previous photo',
    next: 'Next photo',
  },
  horses: {
    title: 'Our horses',
    intro:
      'All eight of our horses are Arabians bred for endurance riding — sure-footed, tireless and kind. Tell us your level and we match you with the right one.',
    calm: 'Calm horses',
    calmDesc: 'For beginners and relaxed intermediate riders. Walk and gentle trot.',
    advanced: 'Horses for experienced riders',
    advancedDesc: 'For confident riders who trot and canter independently.',
    age: '{n} years',
  },
  routes: { forest: 'Forest', sea: 'Sea' },
  rides: {
    title: 'Rides & prices',
    intro: 'Forest or sea. The guide chooses the route of the day depending on weather and ground.',
    forest: 'Forest routes',
    forestDesc: 'Soft paths under pines and birches, sheltered from the wind — our choice on cold or windy days.',
    sea: 'Sea routes',
    seaDesc: 'Through the dunes to a wide sandy beach. Best in calm weather; experienced riders can canter along the waterline.',
    colRide: 'Ride',
    colLevel: 'Level',
    colDuration: 'In the saddle',
    colPrice: 'Price',
    perRider: 'per rider',
    perGroup: 'for two',
    min: 'min',
    book: 'Book',
    prepNote:
      'Plus 30 min before (grooming and tacking up together with your horse) and 30 min after (untacking and care). Included in the price.',
    includedTitle: 'Included in every ride',
    included: [
      'Meeting your horse and a short safety briefing',
      'Grooming and tacking up together',
      'Helmet provided',
      'A short photo session on horseback, if you like',
      'Time with the horses after the ride — brushing and carrots',
      'Tea and coffee in the stable lounge',
    ],
    winterTitle: 'Winter price',
    winterText: 'November – March: {amount} off per rider on every ride. Applied automatically when you book.',
    stebbyTitle: 'Pay with sports compensation',
    stebbyText: 'Our rides can be paid with your employer’s Stebby sports benefit.',
    extrasTitle: 'Also available',
    gift: 'Gift voucher',
    giftDesc: 'Any ride as a gift, valid 12 months.',
    pack: '5-ride pass',
    packDesc: '10% off five rides, shareable within a family.',
    private: 'Private ride',
    privateDesc: 'Just you and the guide — +€20 per ride.',
  },
  booking: {
    title: 'Book a ride',
    intro: 'Choose your ride — the booking calendar opens right here on the page. Pick a date and time, accept the conditions and pay online.',
    steps: [
      { title: 'Choose a ride', text: 'Each button opens the calendar for that exact ride.' },
      { title: 'Pick a date and time', text: 'Only times with daylight and free horses are shown. Mondays are rest days.' },
      { title: 'Confirm and pay', text: 'Accept the conditions and pay by card, Apple Pay or Google Pay. Confirmation comes by email.' },
    ],
    chooseTitle: 'Choose your ride',
    book: 'Book',
    allTimes: 'Not sure yet? See all rides and times',
    assurances: [
      'Free cancellation up to 24 hours before the ride',
      'Bad weather — free rebooking or a full refund',
      'Winter price November – March',
      'Paying with Stebby? Write to us and we’ll send a link.',
    ],
    agreeTitle: 'Before you book',
    agreeIntro: 'Please confirm for every rider in your booking:',
    requirements: 'Every rider is at least 10 years old and weighs under 95 kg.',
    agreeLiability:
      'I understand horse riding carries risk and I ride at my own risk. The stable is not liable for injury or damage.',
    agreeInsurance: 'Every rider in my booking has their own personal accident insurance.',
    agreeCancel: 'Free cancellation up to 24 hours before the ride. Later cancellations are not refunded.',
    mustAgree: 'Please accept all conditions to continue.',
    continue: 'Continue to date and time',
    modalTitle: 'Booking',
    loading: 'Loading the booking calendar…',
    soonTitle: 'Online booking opens soon',
    soonText: 'Until then, book by phone or email — we reply the same day.',
    call: 'Call',
    write: 'Send an email',
    close: 'Close',
  },
  events: {
    title: 'Weddings, photo sessions & celebrations',
    intro:
      'From April to October our horses, hay meadows and forest become your backdrop. Dates for the 2027 season can be booked from 1 November — send a request now to be first in line.',
    from: 'from',
    season: 'Season: April – October',
    request: 'Request a date',
    name: 'Name',
    email: 'Email',
    date: 'Preferred date',
    type: 'Occasion',
    message: 'Tell us about your plans',
    send: 'Send request',
    sent: 'Thank you! We will reply within two days.',
    required: 'Please fill in name, email and date.',
  },
  rules: {
    title: 'Good to know',
    intro: 'Please read before booking — by paying you accept these conditions.',
    items: [
      {
        title: 'Weather',
        text: 'We don’t ride below −15 °C or above +28 °C, in thunderstorms, in winds over 15 m/s or on icy ground. The guide confirms your ride by 9:00 on the day. If we cancel, you get free rebooking or a full refund.',
      },
      {
        title: 'Daylight',
        text: 'We ride only in daylight. In December that is roughly 10:00–15:00, in summer 9:00–21:00. The calendar shows available times automatically.',
      },
      {
        title: 'Cancellation',
        text: 'Free up to 24 hours before the start. Later cancellations are not refunded, but you may pass your place to someone else.',
      },
      {
        title: 'Preparing the horse',
        text: 'Arrive 30 minutes early: we groom the horse and put on the tack together. After the ride another 30 minutes to untack and care for your horse.',
      },
      {
        title: 'Liability & insurance',
        text: 'Horse riding is an activity with increased risk. By booking you agree that you ride at your own risk and the stable bears no liability. Each rider arranges their own accident insurance.',
      },
      {
        title: 'Requirements',
        text: 'Age 10+ (under 16 with a parent’s consent), weight up to 95 kg. We provide helmets. Wear closed shoes with a small heel and clothes for the weather.',
      },
    ],
  },
  footer: {
    tagline: 'Horseback rides in forest and by the sea in Estonia.',
    contact: 'Contact',
    rights: 'All rights reserved.',
  },
};
